import { Link } from 'react-router-dom';
import { CheckCircle2, Coins, Shield, ArrowUpRight } from 'lucide-react';

export default function BlogPolymarketResolutionRedeem() {
  return (
    <div className="min-h-screen bg-dark-900 text-white py-8 px-4 sm:px-6 lg:px-8">
      <main className="max-w-4xl mx-auto space-y-10 animate-fade-in">
        <header className="space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-600/15 border border-emerald-500/40 text-xs text-emerald-100 uppercase tracking-wide">
            <CheckCircle2 className="w-3 h-3" />
            <span>Settlement & redeem</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight">
            Market Resolved on Polymarket? Here&apos;s How Redeem Works (and Why Positions Sometimes Look Weird)
          </h1>
          <p className="text-dark-300 text-sm sm:text-base max-w-2xl">
            Resolution is not the same thing as getting your money back. On Polymarket, winning shares
            become redeemable after settlement, and then you (or BetMimic) has to redeem to pull USDC.e
            back into your wallet. This guide explains the lifecycle and what you should expect.
          </p>
        </header>

        <section className="card space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Coins className="w-5 h-5 text-primary-400" />
            Resolution vs redeem: two separate steps
          </h2>
          <p className="text-sm text-dark-300">
            When a market resolves, the outcome is finalized (YES or NO wins). But your wallet doesn&apos;t
            instantly turn shares into USDC.e. Shares become <span className="font-medium">redeemable</span>, and
            redeem is the action that claims the payout.
          </p>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-2">
            <li>
              <span className="font-medium">Resolved</span>: the event outcome is final, prices are effectively
              0 or 1.
            </li>
            <li>
              <span className="font-medium">Redeemable</span>: your winning shares can be claimed for USDC.e.
            </li>
            <li>
              <span className="font-medium">Redeemed</span>: the USDC.e is back in your wallet.
            </li>
          </ul>
        </section>

        <section className="card space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Shield className="w-5 h-5 text-emerald-400" />
            Why a resolved position can still show up
          </h2>
          <p className="text-sm text-dark-300">
            If a position is resolved but not yet redeemed, you can still see it in your portfolio as a
            claimable balance. Some APIs will also stop serving live prices for resolved tokens, which can
            cause temporary &quot;price not available&quot; messages.
          </p>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-2">
            <li>
              The price endpoint may 404 after resolution. That does not mean you lost funds.
            </li>
            <li>
              The correct behavior is to redeem, not to try to trade it like an active market.
            </li>
            <li>
              BetMimic runs an auto-redeem job for redeemable markets so your wallet stays clean.
            </li>
          </ul>
        </section>

        <section className="card space-y-4">
          <h2 className="text-xl font-semibold">What to do if something looks stuck</h2>
          <ol className="text-sm text-dark-300 list-decimal list-inside space-y-2">
            <li>Wait a bit after resolution; settlement can take time.</li>
            <li>Check whether the position is redeemable in Polymarket.</li>
            <li>If it&apos;s redeemable, redeem and refresh.</li>
            <li>If you see repeated 404 price logs, that&apos;s usually just a resolved market lifecycle issue.</li>
          </ol>
        </section>

        <section className="space-y-3">
          <div className="flex flex-wrap gap-3 text-sm">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-dark-800 border border-dark-600 hover:border-primary-500 transition-colors"
            >
              <ArrowUpRight className="w-4 h-4" />
              Read more Polymarket guides
            </Link>
            <Link
              to="/positions"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary-600/90 hover:bg-primary-500 transition-colors"
            >
              <ArrowUpRight className="w-4 h-4" />
              View positions
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}
